import { isLeft, isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import type {
  HubspotTicketDetailData,
  HubspotTicketStageOptionsResponse,
  UpdateHubspotTicketStatusResponse,
} from '../types';
import { showTicketStatusUpdateToast } from './showTicketStatusUpdateToast';

// Mirrors isApprovedStage in
// api/src/lib/hubspotTicketManagement/operations/updateHubspotTicketStatus.ts
// so client and server agree on which closed stages are approvals.
const isApprovedStageLabel = (label: string | null | undefined) =>
  (label ?? '').trim().toLowerCase() === 'approved';

interface UseHubspotTicketStatusParams {
  ticketId: string;
  trpc: TRPC | null;
  data: HubspotTicketDetailData | null;
  refetchTicket: () => Promise<void>;
  onTicketUpdated?: () => Promise<void>;
}

interface UseHubspotTicketStatusResult {
  statusOptions: HubspotTicketStageOptionsResponse['options'];
  selectedStageId: string;
  currentStageId: string | null;
  statusPipelineLabel: string | null;
  statusError: string | null;
  isStatusLoading: boolean;
  isStatusSaving: boolean;
  isClosingStage: boolean;
  isRejectionClosure: boolean;
  closureCategory: string;
  closureLabel: string;
  closureRejectionReasons: string[];
  reviewerNote: string;
  setSelectedStageId: (stageId: string) => void;
  setClosureCategory: (value: string) => void;
  setClosureLabel: (value: string) => void;
  toggleClosureRejectionReason: (code: string) => void;
  setReviewerNote: (value: string) => void;
  handleSaveStatus: () => Promise<void>;
}

export function useHubspotTicketStatus({
  ticketId,
  trpc,
  data,
  refetchTicket,
  onTicketUpdated,
}: UseHubspotTicketStatusParams): UseHubspotTicketStatusResult {
  const [statusOptions, setStatusOptions] = useState<
    HubspotTicketStageOptionsResponse['options']
  >([]);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [statusPipelineLabel, setStatusPipelineLabel] = useState<string | null>(
    null,
  );
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [closureCategory, setClosureCategory] = useState('');
  const [closureLabel, setClosureLabel] = useState('');
  const [closureRejectionReasons, setClosureRejectionReasons] = useState<
    string[]
  >([]);
  const [reviewerNote, setReviewerNote] = useState('');

  useEffect(() => {
    const fetchStatusOptions = async () => {
      if (!trpc || !trpc.client) {
        setStatusError('TRPC context not initialized');
        setIsStatusLoading(false);
        return;
      }

      setIsStatusLoading(true);
      setIsStatusSaving(false);
      setStatusError(null);
      setStatusOptions([]);
      setSelectedStageId('');
      setCurrentStageId(null);
      setStatusPipelineLabel(null);

      try {
        const result = await trpc.client.getHubspotTicketStageOptions.query({
          ticketId,
        });

        if (isRight(result)) {
          const nextStageId = result.right.currentStageId ?? '';
          setStatusOptions(result.right.options);
          setStatusPipelineLabel(result.right.pipelineLabel);
          setSelectedStageId(nextStageId);
          setCurrentStageId(result.right.currentStageId);
        } else if (isLeft(result)) {
          setStatusError(
            result.left.message || 'Failed to load HubSpot status options',
          );
        }
      } catch (err) {
        captureAndReportError('Error fetching HubSpot status options:', err);
        setStatusError('Failed to load HubSpot status options');
      } finally {
        setIsStatusLoading(false);
      }
    };

    void fetchStatusOptions();
  }, [ticketId, trpc]);

  // Joined into a stable scalar so the reset effect below only re-runs when
  // the codes actually change — `data.rejectionReasonCodes` is a fresh array
  // on every refetch and would clobber in-progress edits otherwise.
  const rejectionReasonCodesKey = (data?.rejectionReasonCodes ?? []).join(',');

  useEffect(() => {
    setClosureCategory(data?.closureDetails?.category ?? '');
    setClosureLabel(data?.closureDetails?.label ?? '');
    // Top-level codes, not closure-scoped: moderator soft-rejects persist a
    // selection before the ticket is closed.
    setClosureRejectionReasons(
      rejectionReasonCodesKey ? rejectionReasonCodesKey.split(',') : [],
    );
  }, [
    data?.closureDetails?.category,
    data?.closureDetails?.label,
    rejectionReasonCodesKey,
    ticketId,
  ]);

  useEffect(() => {
    setReviewerNote('');
  }, [ticketId]);

  const selectedStage =
    statusOptions.find((option) => option.value === selectedStageId) ?? null;
  const isClosingStage = selectedStage?.isClosed ?? false;
  const isRejectionClosure =
    isClosingStage && !isApprovedStageLabel(selectedStage?.label);

  const toggleClosureRejectionReason = (code: string) => {
    setClosureRejectionReasons((currentCodes) =>
      currentCodes.includes(code)
        ? currentCodes.filter((currentCode) => currentCode !== code)
        : [...currentCodes, code],
    );
  };

  const handleSaveStatus = async () => {
    if (
      !trpc?.client ||
      !selectedStageId ||
      selectedStageId === currentStageId
    ) {
      return;
    }

    if (isClosingStage) {
      let validationError: string | null = null;
      if (isRejectionClosure) {
        if (closureRejectionReasons.length === 0 || !reviewerNote.trim()) {
          validationError =
            'Select at least one rejection reason and provide a reviewer note';
        }
      } else if (
        !closureCategory.trim() ||
        !closureLabel.trim() ||
        !reviewerNote.trim()
      ) {
        validationError =
          'Category, label, and reviewer note are required when closing a ticket';
      }

      if (validationError) {
        setStatusError(validationError);
        toast.error(validationError);
        return;
      }
    }

    setIsStatusSaving(true);
    setStatusError(null);

    // Approve-type closures send freeform category/label and an empty codes
    // array so stale rejection reasons from a previous rejected closure get
    // cleared server-side.
    const sendFreeformClosure = isClosingStage && !isRejectionClosure;

    try {
      const result = await trpc.client.updateHubspotTicketStatus.mutate({
        ticketId,
        stageId: selectedStageId,
        closedCategory: sendFreeformClosure
          ? closureCategory.trim()
          : undefined,
        closedLabel: sendFreeformClosure ? closureLabel.trim() : undefined,
        reviewerNote: isClosingStage ? reviewerNote.trim() : undefined,
        rejectionReasonCodes: isClosingStage
          ? isRejectionClosure
            ? closureRejectionReasons
            : []
          : undefined,
      });

      if (isRight(result)) {
        const updatedStatus: UpdateHubspotTicketStatusResponse = result.right;
        setCurrentStageId(updatedStatus.hsPipelineStage);
        setSelectedStageId(updatedStatus.hsPipelineStage ?? '');
        setReviewerNote('');

        const onTicketUpdatedPromise = onTicketUpdated
          ? onTicketUpdated().then(
              () => ({ status: 'fulfilled' as const }),
              (reason) => ({ status: 'rejected' as const, reason }),
            )
          : Promise.resolve({ status: 'fulfilled' as const });

        await refetchTicket();

        const nextStageLabel =
          statusOptions.find(
            (option) => option.value === updatedStatus.hsPipelineStage,
          )?.label ??
          updatedStatus.mappedSubmissionStatus ??
          'selected stage';

        showTicketStatusUpdateToast({
          baseMessage: `Ticket status updated to ${nextStageLabel}`,
          approvalEmail: updatedStatus.approvalEmail,
          rejectionEmail: updatedStatus.rejectionEmail,
        });

        const onTicketUpdatedResult = await onTicketUpdatedPromise;
        if (onTicketUpdatedResult.status === 'rejected') {
          throw onTicketUpdatedResult.reason;
        }
      } else if (isLeft(result)) {
        setStatusError(
          result.left.message || 'Failed to update HubSpot ticket status',
        );
        toast.error(
          result.left.message || 'Failed to update HubSpot ticket status',
        );
      }
    } catch (err) {
      captureAndReportError('Error updating HubSpot ticket status:', err);
      setStatusError('Failed to update HubSpot ticket status');
      toast.error('Failed to update HubSpot ticket status');
    } finally {
      setIsStatusSaving(false);
    }
  };

  return {
    statusOptions,
    selectedStageId,
    currentStageId,
    statusPipelineLabel,
    statusError,
    isStatusLoading,
    isStatusSaving,
    isClosingStage,
    isRejectionClosure,
    closureCategory,
    closureLabel,
    closureRejectionReasons,
    reviewerNote,
    setSelectedStageId,
    setClosureCategory,
    setClosureLabel,
    toggleClosureRejectionReason,
    setReviewerNote,
    handleSaveStatus,
  };
}
